import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { broadcastQueryData } from 'src/composables/app/system/useBroadcastChannel'
import { userService } from 'src/services/api/user.service'
import { log } from 'src/services/app/log.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { MUTATION_KEYS, QUERY_KEYS } from 'src/services/consts/queries.const'
import { USER_FUNCTION_STATUS } from 'src/services/consts/user.const'
import { wrapMutationAsync } from 'src/services/app/util.service'

/**
 * Verifies a pending additional email with the emailed 6-digit code.
 * Every envelope that carries `userInfo` refreshes the cache — including
 * `invalid` / `expired`, where the server may have already dropped the
 * pending entry. Those two statuses show inline feedback in the modal,
 * so they intentionally produce no toast.
 */
export function useVerifyAdditionalEmailMutation() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationKey: [MUTATION_KEYS.VERIFY_ADDITIONAL_EMAIL],
    mutationFn: ({ email, code }) => userService.verifyAdditionalEmail({ email, code }),

    onSuccess: ({ status, userInfo }) => {
      if (userInfo != null) {
        queryClient.setQueryData([QUERY_KEYS.USER_INFO], userInfo)
        broadcastQueryData([QUERY_KEYS.USER_INFO], userInfo)
      }
      if (status !== USER_FUNCTION_STATUS.SUCCESS) return
      notifyService.success(notifyMsgs.success.additionalEmailVerified)
    },

    onError: (error) => {
      log.error('[VerifyAdditionalEmail] Failed to verify code', error)
      notifyService.error(notifyMsgs.error.verifyAdditionalEmail)
    },
  })

  return {
    verifyAdditionalEmail: wrapMutationAsync(mutation.mutateAsync),
    isVerifyingAdditionalEmail: mutation.isPending,
  }
}
